import Player from '@vimeo/player';

/**
 * The Video class works in conjunction with the video snippet. It scrapes data
 * from the snippet and instantiates a custom Vimeo player on that data.
 * It handles custom play/pause and mute/unmute buttons, pausing loop after 3 minutes,
 * and only autoplaying when in viewport.
 */
export default class Video {
  /**
   * @param {DOMnode} element top-level node for the section/component
   * @param {DOMnode} video top-level node for the video
   * @param {Class} layout for handling breakpoints
   */
  constructor({ element, video, layout }) {
    const dataEl = video.querySelector('[data-component-data]');

    this.element = element;
    this.video = video;
    this.layout = layout;
    this.data = dataEl ? JSON.parse(dataEl.textContent) : {};
    this.isMuted = true;
    this.isMobile = true;
    this.isPlaying = false;
    this.hasMultipleVideos =
      !!this.data.videoDesktop && !!this.data.videoMobile;
    this.loopTimeout = null;
    this.intersectionObserver = null;
    this.allowAutoplay = true;

    this.handlePlay = this.handlePlay.bind(this);
    this.handleMute = this.handleMute.bind(this);
    this.handleBpChange = this.handleBpChange.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleIntersection = this.handleIntersection.bind(this);

    this.playEl = this.data.play?.selector
      ? element.querySelector(this.data.play.selector)
      : video.querySelector('[data-video-play]');
    this.muteEl = this.data.mute?.selector
      ? element.querySelector(this.data.mute.selector)
      : video.querySelector('[data-video-mute]');

    this.init();
  }

  handlePlay() {
    if (this.isPlaying) {
      this.pauseVideo();
    } else this.playVideo();
    if (this.playEl)
      [...this.playEl.children].forEach((child) =>
        child.classList.toggle('hidden'),
      );
  }

  async pauseVideo() {
    try {
      const status = await this.player.getPaused?.();
      if (status === true) return;

      this.isPlaying = false;
      await this.player.pause();
      this.playEl?.setAttribute(
        'aria-label',
        this.data.play?.playText || 'Play',
      );

      if (this.loopTimeout) {
        clearTimeout(this.loopTimeout);
        this.loopTimeout = null;
      }
    } catch (error) {
      console.warn('pauseVideo error:', error);
    }
  }

  async playVideo() {
    try {
      const status = await this.player.getPaused?.();
      if (status === false) return;

      this.isPlaying = true;
      await this.player.play();
      this.playEl?.setAttribute(
        'aria-label',
        this.data.play?.pauseText || 'Pause',
      );
      this.setLoopTimeout();
    } catch (error) {
      console.warn('playVideo error:', error);
    }
  }

  setLoopTimeout() {
    if (this.loopTimeout) clearTimeout(this.loopTimeout);

    this.loopTimeout = setTimeout(() => {
      this.pauseVideo();
      this.allowAutoplay = false;

      if (this.playEl) {
        this.playEl.children[0].classList.remove('hidden');
        this.playEl.children[1].classList.add('hidden');
      }
    }, 60 * 3 * 1000); // 3 minutes
  }

  handleMute() {
    if (this.isMuted) {
      this.unmuteVideo();
    } else {
      this.muteVideo();
    }

    if (this.muteEl)
      [...this.muteEl.children].forEach((child) =>
        child.classList.toggle('hidden'),
      );
  }

  unmuteVideo() {
    if (this.data.host === 'shopify') {
      this.player.muted = false;
    } else if (this.data.host === 'vimeo') {
      this.player.setVolume(1);
    }
    this.isMuted = false;
    this.muteEl?.setAttribute('aria-label', this.data.mute?.muteText || 'Mute');
  }

  muteVideo() {
    if (this.data.host === 'shopify') {
      this.player.muted = true;
    } else if (this.data.host === 'vimeo') {
      this.player.setVolume(0);
    }
    this.isMuted = true;
    this.muteEl?.setAttribute(
      'aria-label',
      this.data.mute?.unmuteText || 'Unmute',
    );
  }

  resetPlayer() {
    this.unbindEventListeners();

    if (this.loopTimeout) {
      clearTimeout(this.loopTimeout);
      this.loopTimeout = null;
    }

    if (this.data.host === 'shopify') {
      this.resetShopifyPlayer();
    } else if (this.data.host === 'vimeo') {
      this.resetVimeoPlayer();
    }
  }

  unbindEventListeners() {
    this.playEl?.removeEventListener('click', this.handlePlay);
    this.muteEl?.removeEventListener('click', this.handleMute);
    if (this.layout) {
      window.removeEventListener('bpChange', this.handleBpChange);
      window.removeEventListener('resize', this.handleResize);
    }

    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
  }

  resetShopifyPlayer() {
    this.resetControlButtons();
  }

  resetVimeoPlayer() {
    this.player.destroy().then(() => {
      this.resetControlButtons();
      this.init();
    });
  }

  resetControlButtons() {
    this.isPlaying = false;
    this.playEl?.setAttribute('aria-label', this.data.play?.playText || 'Play');

    if (this.playEl?.children) {
      this.playEl.children[0].classList.remove('hidden');
      this.playEl.children[1].classList.add('hidden');
    }

    this.isMuted = true;
    this.muteEl?.setAttribute(
      'aria-label',
      this.data.mute?.unmuteText || 'Unmute',
    );

    if (this.muteEl?.children) {
      this.muteEl.children[0].classList.remove('hidden');
      this.muteEl.children[1].classList.add('hidden');
    }
  }

  handleBpChange() {
    if (this.isMobile && this.layout.isAboveBreakpoint('sm')) {
      this.isMobile = false;
      this.resetPlayer();
    } else if (!this.isMobile && !this.layout.isAboveBreakpoint('sm')) {
      this.isMobile = true;
      this.resetPlayer();
    }
  }

  handleResize() {
    if (this.layout.isAboveBreakpoint('sm')) {
      this.isMobile = false;
      this.resetPlayer();
    } else {
      this.isMobile = true;
      this.resetPlayer();
    }
  }

  handleIntersection(entries) {
    const entry = entries[0];

    if (entry.isIntersecting) {
      if (!this.isPlaying && this.data.autoplay && this.allowAutoplay) {
        this.playVideo();
        if (this.playEl?.children) {
          this.playEl.children[0].classList.add('hidden');
          this.playEl.children[1].classList.remove('hidden');
        }
      }
    } else if (this.isPlaying) {
      this.pauseVideo();
      if (this.playEl?.children) {
        this.playEl.children[0].classList.remove('hidden');
        this.playEl.children[1].classList.add('hidden');
      }
    }
  }

  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '70px',
      threshold: 0.1,
    };

    this.intersectionObserver = new IntersectionObserver(
      this.handleIntersection,
      options,
    );
    this.intersectionObserver.observe(this.element);
  }

  init() {
    if (this.layout && this.hasMultipleVideos) {
      if (this.layout.isAboveBreakpoint('sm')) {
        if (this.data.videoDesktop && this.data.videoDesktop !== 'false') {
          this.initVideoByType(this.data.videoDesktop);
          this.isMobile = false;
        }
      } else if (this.data.videoMobile && this.data.videoMobile !== 'false') {
        this.initVideoByType(this.data.videoMobile);
        this.isMobile = true;
      }
    } else if (this.data.videoDesktop && this.data.videoDesktop !== 'false') {
      this.initVideoByType(this.data.videoDesktop);
    } else if (this.data.videoMobile && this.data.videoMobile !== 'false') {
      this.initVideoByType(this.data.videoMobile);
    }

    this.setupIntersectionObserver();
  }

  initVideoByType(vimeoVideoId) {
    if (this.data.host === 'shopify') {
      this.initShopifyVideo(this.data.videoId);
    } else if (this.data.host === 'vimeo') {
      this.initVimeoVideo(vimeoVideoId, this.data.videoId);
    }
  }

  calculateVideoHeight() {
    let height = this.element.offsetHeight;
    if (height < this.element.offsetWidth) {
      height *= this.element.offsetWidth / height;
    }
    return height;
  }

  onTimeUpdate = (vimeoPlayer) => {
    vimeoPlayer.getCurrentTime().then((currentTime) => {
      const roundedTime = Math.floor(currentTime);
      // Detect if the video is playing based on the roundedTime value
      if (roundedTime > 0) {
        // If the video is playing, add a class to the video container if its present
        const videoContainer = this.element.querySelector(
          '.hero-secondary__video',
        );
        if (
          videoContainer &&
          !videoContainer.classList.contains('video-playing')
        ) {
          videoContainer.classList.add('video-playing');
        }
      }
    });
  };

  initVimeoVideo(vimeoVideoId, elementId) {
    const videoEl = document.querySelector(`#${elementId}`);
    if (
      !videoEl ||
      getComputedStyle(videoEl.closest('[data-component="video"]'))?.display ===
        'none'
    )
      return;
    this.player = new Player(videoEl, {
      id: vimeoVideoId,
      controls: false,
      autoplay: false,
      muted: true,
      responsive: true,
      loop: true,
      texttrack: this.data.iso_code + this.data.country,
      ...(!this.layout.isAboveBreakpoint('sm') &&
        this.data.mobileFullHeight && {
          height: this.calculateVideoHeight(),
          responsive: false,
        }),
    });
    this.player.on('timeupdate', () => this.onTimeUpdate(this.player));

    this.player.on('ended', () => {
      if (this.loopTimeout) {
        clearTimeout(this.loopTimeout);
        this.loopTimeout = null;
      }
      this.pauseVideo();
      if (this.playEl?.children) {
        this.playEl.children[0].classList.remove('hidden');
        this.playEl.children[1].classList.add('hidden');
      }
    });

    this.bindEventListeners();
  }

  initShopifyVideo(id) {
    if (!document.querySelector(`#${id} video`)) return;
    this.player = this.element.querySelector(`#${id} video`);

    this.player.addEventListener('ended', () => {
      if (this.loopTimeout) {
        clearTimeout(this.loopTimeout);
        this.loopTimeout = null;
      }
      this.pauseVideo();
      if (this.playEl?.children) {
        this.playEl.children[0].classList.remove('hidden');
        this.playEl.children[1].classList.add('hidden');
      }
    });

    this.bindEventListeners();
  }

  bindEventListeners() {
    this.playEl?.addEventListener('click', this.handlePlay);
    this.muteEl?.addEventListener('click', this.handleMute);
    if (this.layout && this.hasMultipleVideos) {
      if (this.data.mobileFullHeight) {
        window.addEventListener('resize', () => {
          if (this.timerId) clearTimeout(this.timerId);
          this.timerId = setTimeout(() => {
            this.handleResize();
          }, 500);
        });
      } else {
        window.addEventListener('bpChange', this.handleBpChange);
      }
    }
  }
}
